/**
 * OneTrust Analytics Router
 *
 * PERMANENT MODULE - Handles GDPR-compliant event routing for OneTrust consent manager.
 *
 * After productization (~2 weeks post-rollout):
 * - This module stays permanent
 * - Statsig gate will be removed from codebase
 * - OneTrust will be the only consent manager
 * - This router continues handling per-event routing decisions
 *
 * Responsibilities:
 * - Check if OneTrust stub is active (user hasn't consented)
 * - Filter events by safe-list during stub phase
 * - Drop non-safe-list events before consent (GDPR compliant)
 * - Route safe-list events server-side before consent (business necessity)
 * - Pass through all events after consent (real analytics loaded)
 */

import type { OneTrustGateHandler } from './oneTrustGateHandler';
import { isServerSideAllowedEvent } from './serverSideEventAllowList';

// Routing decision action constants
const ROUTING_ACTION = {
  DROP: 'drop',
  ROUTE_SERVER_SIDE: 'route-server-side',
  PASS_THROUGH: 'pass-through',
} as const;

export type OneTrustRoutingDecision =
  | { action: typeof ROUTING_ACTION.DROP; reason: string }
  | { action: typeof ROUTING_ACTION.ROUTE_SERVER_SIDE; reason: string }
  | { action: typeof ROUTING_ACTION.PASS_THROUGH; reason: string };

export class OneTrustAnalyticsRouter {
  constructor(private gateHandler: OneTrustGateHandler) {}

  /**
   * Determine what to do with a track event in the OneTrust context
   *
   * @param event - The event name to check
   * @returns Decision object indicating whether to drop, route server-side, or pass through
   */
  routeTrackEvent(event: string): OneTrustRoutingDecision {
    // Check if OneTrust stub is active (user hasn't consented yet)
    if (!this.gateHandler.isOneTrustAnalyticsStub()) {
      // Check allowlist for legacy/post-consent path
      if (isServerSideAllowedEvent(event)) {
        return {
          action: ROUTING_ACTION.ROUTE_SERVER_SIDE,
          reason: 'Event on server-side allowlist',
        };
      }
      return {
        action: ROUTING_ACTION.PASS_THROUGH,
        reason: 'OneTrust not active or real analytics loaded',
      };
    }

    // Stub is active - only allow safe-list events
    if (!isServerSideAllowedEvent(event)) {
      return {
        action: ROUTING_ACTION.DROP,
        reason: 'OneTrust stub active, event not on safe-list (GDPR compliant)',
      };
    }

    // Safe-list event during stub phase - route server-side
    return {
      action: ROUTING_ACTION.ROUTE_SERVER_SIDE,
      reason: 'OneTrust stub active, event on safe-list (business necessity)',
    };
  }

  /**
   * Determine what to do with a page event in the OneTrust context
   *
   * @returns Decision object indicating whether to drop, route server-side, or pass through
   */
  routePageEvent(): OneTrustRoutingDecision {
    // Check if OneTrust stub is active (user hasn't consented yet)
    if (!this.gateHandler.isOneTrustAnalyticsStub()) {
      // Check allowlist for legacy/post-consent path
      if (isServerSideAllowedEvent('page')) {
        return {
          action: ROUTING_ACTION.ROUTE_SERVER_SIDE,
          reason: 'Page event on server-side allowlist',
        };
      }
      return {
        action: ROUTING_ACTION.PASS_THROUGH,
        reason: 'OneTrust not active or real analytics loaded',
      };
    }

    // Stub is active - only allow safe-list events
    if (!isServerSideAllowedEvent('page')) {
      return {
        action: ROUTING_ACTION.DROP,
        reason:
          'OneTrust stub active, page event not on safe-list (GDPR compliant)',
      };
    }

    // Safe-list page event during stub phase - route server-side
    return {
      action: ROUTING_ACTION.ROUTE_SERVER_SIDE,
      reason:
        'OneTrust stub active, page event on safe-list (business necessity)',
    };
  }

  /**
   * Determine what to do with an identify event in the OneTrust context
   *
   * @returns Decision object indicating whether to drop, route server-side, or pass through
   */
  routeIdentifyEvent(): OneTrustRoutingDecision {
    // Check if OneTrust stub is active (user hasn't consented yet)
    if (!this.gateHandler.isOneTrustAnalyticsStub()) {
      // Check allowlist for legacy/post-consent path
      if (isServerSideAllowedEvent('identify')) {
        return {
          action: ROUTING_ACTION.ROUTE_SERVER_SIDE,
          reason: 'Identify event on server-side allowlist',
        };
      }
      return {
        action: ROUTING_ACTION.PASS_THROUGH,
        reason: 'OneTrust not active or real analytics loaded',
      };
    }

    // Stub is active - only allow safe-list events
    if (!isServerSideAllowedEvent('identify')) {
      return {
        action: ROUTING_ACTION.DROP,
        reason:
          'OneTrust stub active, identify event not on safe-list (GDPR compliant)',
      };
    }

    // Safe-list identify event during stub phase - route server-side
    return {
      action: ROUTING_ACTION.ROUTE_SERVER_SIDE,
      reason:
        'OneTrust stub active, identify event on safe-list (business necessity)',
    };
  }
}
